/*!
 * egt.podcasts.views — the podcast pages (spec 35 WP2; approved mockups D037/D038).
 *
 *   index    /podcasts                 hero + one whole-card link per podcast
 *   show     /podcasts/<show>          album-style hero, seasons, start here, series,
 *                                      every episode, sign-up / follow, back link
 *   episode  /podcasts/<show>/<slug>   split hero + theatre play, keep watching,
 *                                      player + chapters, quotes, show notes, hosts,
 *                                      previous / next, sign-up, transcript, back link
 *
 * Runs server-side in the render worker and returns UA/JSON for S.page(), so the
 * pages carry the site's own nav, footer, capture and reveal. Data arrives from
 * app/routes/egt.content.pages.js:
 *   data.payload  the record (episode) — absent on hubs
 *   data.extra    { catalog, show, cards } — cards are light episode summaries the
 *                 viewer may link to (published only for the public)
 *
 * Never link to a page the viewer cannot open: an episode without a page links to
 * YouTube (or Apple) instead, and related / previous / next cards only show
 * episodes that have one.
 */
var S = require("./egt.sections.js");
var Notes = require("./egt.content.notes.js");   // dependency-free: this file is in the browser bundle

var FALLBACK_BG = "/imgs/egt-platform-canvas.jpg";
var FOUNDER_PORTRAIT = "/imgs/founder/justin.jpg";

/* Design copy that belongs to the page, not to the content records. */
var SHOW_EXTRAS = {
  "create": {
    capture: {
      source: "podcast", id: "egt-cap-podcast", eyebrow: "The next season",
      title: [ "Making things with AI, behind the scenes. ", { n: "span", c: "egt__gold", i: "Get the first episode." } ],
      lead: "Season 0 is 40 episodes on marketing, branding and pricing. The next season goes behind the scenes on making things with AI. Leave your email and the first new episode comes straight to you.",
      label: "Where should we send it?", cta: "Send me the first episode",
      consentLabel: "Yes — email me the first episode of the new Create_ season, then new episodes and news from EGT. I can unsubscribe from any email."
    }
  },
  "classic-man": {
    hostsTitle: [ "Two men. ", { n: "span", c: "egt__gold", i: "One mission." } ],
    hostsLead: "Strength with a conscience: service, faith, and the hard-won kind of wisdom you can only earn.",
    classicman: true
  }
};

/* ── small helpers ────────────────────────────────────────────────────────── */

/** Only https:// links and site paths ever become an href (the schema checks too). */
function safeUrl(u, allowSite) {
  if (typeof u !== "string") { return null; }
  if (/^https:\/\/[^\s"'<>\\]+$/i.test(u)) { return u; }
  if (allowSite && /^\/(?![\/\\])[^\s"'<>\\]*$/.test(u)) { return u; }
  return null;
}
exports.safeUrl = safeUrl;

function img(u, fallback) { return (typeof u === "string" && /^(\/(?!\/)|https:\/\/)/.test(u)) ? u : (fallback || FALLBACK_BG); }

/** "2025-12-10" → "10 Dec 2025" (date-only values are calendar dates, not instants). */
function fmtDay(d) {
  if (!d) { return ""; }
  var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d));
  if (!m) { return ""; }
  var dt = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return dt.toLocaleDateString("en-GB", { timeZone: "UTC", day: "numeric", month: "short", year: "numeric" });
}
exports.fmtDay = fmtDay;

/** 3725 → "1:02:05", 84 → "1:24". */
function fmtTs(t) {
  t = Math.max(0, Math.floor(Number(t) || 0));
  var h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), s = t % 60;
  var ss = (s < 10 ? "0" : "") + s;
  return h ? h + ":" + (m < 10 ? "0" : "") + m + ":" + ss : m + ":" + ss;
}
exports.fmtTs = fmtTs;

function isoDuration(sec) {
  sec = Math.max(0, Math.floor(Number(sec) || 0));
  var h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  return "PT" + (h ? h + "H" : "") + (m ? m + "M" : "") + (s || (!h && !m) ? s + "S" : "");
}

function hoursMinutes(sec) {
  var h = Math.floor(sec / 3600), m = Math.round((sec % 3600) / 60);
  if (m === 60) { h += 1; m = 0; }
  return h ? h + " h" + (m ? " " + m + " min" : "") : m + " min";
}

function ytWatch(id, t) { return "https://www.youtube.com/watch?v=" + encodeURIComponent(id) + (t !== undefined ? "&t=" + Math.floor(t) + "s" : ""); }
function ytEmbed(id) { return "https://www.youtube-nocookie.com/embed/" + encodeURIComponent(id) + "?rel=0"; }

function joinMeta(parts) { return parts.filter(Boolean).join(" · "); }

/** Where an episode card should go: its page when the viewer can open it, else off-site. */
function cardHref(c) {
  if (c.page) { return c.url_path; }
  return safeUrl(c.youtube_url) || safeUrl(c.apple_url) || null;
}
function offsite(href) { return /^https?:\/\//.test(href || ""); }
function linkAttrs(node, href) {
  node.href = href;
  if (offsite(href)) { node.target = "_blank"; node.rel = "noopener"; }
  return node;
}

/* glyphs */
function playGlyph(cls) {
  return { n: "svg", c: cls || "", viewBox: "0 0 24 24", "aria-hidden": "true", focusable: "false",
    i: { n: "path", fill: "currentColor", d: "M8 5v14l11-7z" } };
}
function ytGlyph() {
  return { n: "svg", c: "egt-stream__logo", viewBox: "0 0 24 24", "aria-hidden": "true", focusable: "false",
    i: { n: "path", fill: "currentColor",
      d: "M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z" } };
}
function podcastGlyph() {
  return { n: "svg", c: "egt-stream__logo", viewBox: "0 0 24 24", "aria-hidden": "true", focusable: "false",
    i: { n: "path", fill: "currentColor",
      d: "M12 1a9 9 0 0 0-3.4 17.3l.4-1.9A7.1 7.1 0 1 1 15 16.4l.4 1.9A9 9 0 0 0 12 1zm0 4a5 5 0 0 0-2.6 9.3l.4-2a3.1 3.1 0 1 1 4.4 0l.4 2A5 5 0 0 0 12 5zm0 4.6a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2zM10.6 14l-.9 7.2c-.1.9.6 1.8 1.6 1.8h1.4c1 0 1.7-.9 1.6-1.8l-.9-7.2a2.5 2.5 0 0 1-2.8 0z" } };
}
function closeGlyph() {
  return { n: "svg", viewBox: "0 0 24 24", "aria-hidden": "true", focusable: "false",
    i: { n: "path", d: "M6 6l12 12M18 6L6 18", stroke: "currentColor", "stroke-width": "1.6", "stroke-linecap": "round", fill: "none" } };
}

/** Icon-only stream pills for show heroes (order from the record; hidden platforms skipped). */
function streamPills(show) {
  var raw = (show && show.platforms) || {};
  var pf = { order: raw.order, youtube: safeUrl(raw.youtube), apple: safeUrl(raw.apple), spotify: safeUrl(raw.spotify), rss: safeUrl(raw.rss) };
  var hidden = {};
  ((show && show.platforms_hidden) || []).forEach(function (h) { if (h && h.platform) { hidden[h.platform] = true; } });
  var order = pf.order || [ "youtube", "apple", "spotify" ];
  var out = [];
  order.forEach(function (k) {
    if (hidden[k] || !pf[k]) { return; }
    if (k === "youtube") { out.push(S.streamPill(pf.youtube, "YouTube", ytGlyph(), "youtube")); }
    if (k === "apple") { out.push(S.streamPill(pf.apple, "Apple Podcasts", podcastGlyph(), "apple")); }
    if (k === "spotify") { out.push(S.streamPill(pf.spotify, "Spotify", S.glyphs.spotify(), "spotify")); }
  });
  return out;
}

function sectionHead(eyebrow, title, lead) {
  return { n: "div", c: "egt-section__head egt-reveal", i: [
    { n: "p", c: "egt__eyebrow", i: eyebrow },
    { n: "h2", c: "egt-section__title", i: title }
  ].concat(lead ? [ { n: "p", c: "egt-section__lead", i: lead } ] : []) };
}

function backLink(href, label) {
  return { n: "section", c: "egt-section egt-album-back", i: { n: "div", c: "egt__shell egt-reveal",
    i: { n: "a", href: href, c: "egt-album-back__link", i: [ { n: "span", c: "egt-album-back__arrow", "aria-hidden": "true", i: "‹ " }, label ] } } };
}

function scripts() {
  return [ { n: "script", src: "/src/egt.home.js", type: "text/javascript" },
           { n: "script", src: "/src/egt.content.js", type: "text/javascript", defer: "defer" },
           { n: "script", src: "/src/egt.podcasts.js", type: "text/javascript", defer: "defer" },
           { n: "script", src: "/src/egt.scrollhint.js", type: "text/javascript", defer: "defer" } ];
}

function bannerNode(banner) {
  if (!banner || !banner.text) { return null; }
  return { n: "div", c: "egt-cpreview egt-cpreview--" + (banner.tone || "info"), role: "status",
    i: [ { n: "span", c: "egt-cpreview__dot", "aria-hidden": "true" }, { n: "span", c: "egt-cpreview__text", i: banner.text } ] };
}

function page(sections, banner, schema) {
  var b = bannerNode(banner);
  return S.page("/reserve", (b ? [ b ] : []).concat(sections).concat(scripts()).concat(schema ? [ schema ] : []));
}

function showOf(extra, key) {
  var cat = (extra && extra.catalog) || {};
  return (cat.shows || []).filter(function (s) { return s.key === key; })[0] || null;
}

function cardsBySlug(cards) {
  var map = {};
  (cards || []).forEach(function (c) { map[c.slug] = c; });
  return map;
}

function hostLine(show) {
  var names = ((show && show.hosts) || []).map(function (h) { return h.name; });
  if (!names.length) { return ""; }
  return "hosted by " + (names.length > 1 ? names.slice(0, -1).join(", ") + " and " + names[names.length - 1] : names[0]);
}

/* ── track rows (show page "All episodes") ────────────────────────────────── */

function playLead(no) {
  return { n: "span", c: "egt-album-trk__lead", i: [
    { n: "span", c: "egt-album-play egt-pod-play", "aria-hidden": "true", i: [
      { n: "svg", c: "egt-album-play__ring", viewBox: "0 0 36 36", "aria-hidden": "true", focusable: "false",
        i: { n: "circle", c: "egt-album-play__ring-track", cx: "18", cy: "18", r: "16", fill: "none" } },
      { n: "span", c: "egt-album-play__glyphs", i: playGlyph("egt-album-play__glyph egt-album-play__glyph--play") }
    ] },
    { n: "span", c: "egt-album-trk__no", "aria-hidden": "true", i: String(no || "") }
  ] };
}

function episodeRow(c, i) {
  var href = cardHref(c);
  var meta = joinMeta([ fmtDay(c.date), c.runtime_display ]);
  var inner = [ playLead(c.number), { n: "span", c: "egt-album-trk__body", i: [
    { n: "span", c: "egt-album-trk__titlerow", i: { n: "span", c: "egt-album-trk__title", i: c.title || "" } },
    { n: "span", c: "egt-album-trk__feeling egt-pod-meta", i: meta + (href && !c.page ? " · " + (safeUrl(c.youtube_url) ? "on YouTube" : "on Apple Podcasts") : "") }
  ] } ];
  var row = href
    ? linkAttrs({ n: "a", c: "egt-pod-trk", "aria-label": (c.title || "") + ", " + meta, i: inner }, href)
    : { n: "span", c: "egt-pod-trk", i: inner };
  return { n: "li", c: "egt-album-trk egt-reveal", style: "--egt-i: " + Math.min(i, 12), i: row };
}

/* ── index /podcasts ──────────────────────────────────────────────────────── */

function indexView(data, banner) {
  var x = data.extra || {};
  var cat = x.catalog || {};
  var idx = cat.index || {};
  var counts = x.counts || {};
  var cards = (cat.shows || []).map(function (s, i) {
    var n = counts[s.key] || 0;
    return { n: "a", c: "egt-feat egt-pod-show egt-reveal", style: "--egt-i: " + i, href: s.url_path || ("/podcasts/" + s.key), i: [
      { n: "img", c: "egt-pod-show__img", src: img(s.cover), alt: s.name + " cover art", loading: "lazy", decoding: "async", width: "600", height: "600" },
      { n: "span", c: "egt-pod-show__body", i: [
        { n: "span", c: "egt-card__k", i: s.kicker || "An EGT podcast" },
        { n: "span", c: "egt-feat__title", i: s.name },
        { n: "span", c: "egt-feat__copy", i: s.show_line || "" },
        { n: "span", c: "egt-pod-show__count", i: n + (n === 1 ? " episode" : " episodes") },
        { n: "span", c: "egt-cta", i: [ "Listen", { n: "span", c: "egt-cta__tick", "aria-hidden": "true", i: " ›" } ] }
      ] }
    ] };
  });
  var sections = [
    { n: "section", c: "egt-section egt-canvas egt-canvas--center egt-pod-ixhero", i: [
      S.media({ img: img(idx.background), alt: "", priority: true, parallax: 0.06 }),
      { n: "div", c: "egt__shell egt-canvas__inner", i: S.glass({ width: "wide", c: "egt-reveal", content: [
        { n: "p", c: "egt__eyebrow", i: idx.eyebrow || "EGT Podcasts" },
        { n: "h1", c: "egt-album-hero__title", i: idx.title || "Podcasts" },
        idx.lead ? { n: "p", c: "egt-pod-ix-lead", i: idx.lead } : ""
      ] }) }
    ] },
    { n: "section", c: "egt-section", i: { n: "div", c: "egt__shell", i: [
      sectionHead("The podcasts", idx.section_title || "Our podcasts"),
      { n: "div", c: "egt-grid egt-pod-shows", i: cards }
    ] } }
  ];
  var O = S.siteOrigin();
  var schema = S.jsonLd([
    { "@type": "CollectionPage", "@id": O + "/podcasts#webpage", url: O + "/podcasts", name: idx.title || "Podcasts",
      description: idx.lead || "", isPartOf: { "@id": O + "/#website" },
      hasPart: (cat.shows || []).map(function (s) { return { "@id": O + (s.url_path || "/podcasts/" + s.key) + "#podcast" }; }) },
    breadcrumbs([ { name: "Podcasts", url: "/podcasts" } ])
  ]);
  return page(sections, banner, schema);
}

function breadcrumbs(list) {
  var O = S.siteOrigin();
  return { "@type": "BreadcrumbList", itemListElement: [ { name: "Home", url: "/" } ].concat(list).map(function (c, i) {
    return { "@type": "ListItem", position: i + 1, name: c.name, item: O + c.url };
  }) };
}

/* ── show /podcasts/<show> ────────────────────────────────────────────────── */

function showHero(show, count) {
  return { n: "section", c: "egt-section egt-canvas egt-canvas--center egt-canvas--tall egt-album-hero", i: [
    S.media({ img: img(show.background), alt: "", priority: true, parallax: 0.06 }),
    { n: "div", c: "egt__shell egt-canvas__inner", i: { n: "div", c: "egt-glass egt-glass--wide egt-album-hero__card egt-reveal", i: [
      { n: "div", c: "egt-album-hero__cover", i: { n: "img", c: "egt-album-hero__img", src: img(show.cover), alt: show.name + " cover art",
        width: "600", height: "600", loading: "eager", decoding: "async" } },
      { n: "div", c: "egt-album-hero__meta", i: [
        { n: "p", c: "egt__eyebrow", i: show.kicker || "An EGT podcast" },
        { n: "h1", c: "egt-album-hero__title", i: show.name },
        show.show_line ? { n: "p", c: "egt-album-hero__sub", i: show.show_line } : "",
        { n: "p", c: "egt-album-hero__count", i: joinMeta([ count + (count === 1 ? " episode" : " episodes"), hostLine(show) ]) },
        { n: "div", c: "egt-album-hero__posture", i: { n: "div", c: "egt-album-hero__stream", role: "group", "aria-label": "Listen to " + show.name, i: streamPills(show) } }
      ] }
    ] } }
  ] };
}

function seasonsSection(show) {
  if (!show.seasons || !show.seasons.length) { return null; }
  return { n: "section", c: "egt-section egt-pod-eras", i: { n: "div", c: "egt__shell", i: [
    sectionHead(show.seasons.length === 2 ? "Two seasons" : "Seasons", "The archive, and what comes next"),
    { n: "div", c: "egt-grid egt-pod-eras__grid egt-reveal", i: show.seasons.map(function (s, i) {
      var next = i > 0 && /soon/i.test(s.state || "");
      return { n: "article", c: "egt-feat egt-pod-era" + (next ? " egt-pod-era--next" : ""), i: [
        { n: "p", c: "egt-card__k", i: s.kicker },
        { n: "h3", c: "egt-feat__title", i: s.title },
        { n: "p", c: "egt-feat__copy", i: s.copy },
        { n: "p", c: "egt-pod-era__state", i: next ? [ { n: "span", c: "egt-pod-dot", "aria-hidden": "true" }, s.state ] : s.state },
        { n: "a", c: "egt-cta", href: next ? "#join" : "#episodes", i: [ next ? "Get the first episode" : "Browse the archive", { n: "span", c: "egt-cta__tick", "aria-hidden": "true", i: " ›" } ] }
      ] };
    }) }
  ] } };
}

function startHere(show, bySlug, altBackground) {
  var picks = (show.start_here || []).map(function (s) { return bySlug[s]; }).filter(function (c) { return c && cardHref(c); });
  if (!picks.length) { return null; }
  return { n: "section", c: "egt-section egt-canvas egt-canvas--center egt-pod-start", i: [
    S.media({ img: img(altBackground || show.background), alt: "", parallax: 0.06 }),
    { n: "div", c: "egt__shell egt-canvas__inner egt-pod-start__inner", i: { n: "div", c: "egt-glass egt-launch egt-reveal", i: [
      { n: "div", c: "egt-launch__bar", i: [
        { n: "span", c: "egt-launch__lights", "aria-hidden": "true", i: [
          { n: "span", c: "egt-launch__dot egt-launch__dot--r" }, { n: "span", c: "egt-launch__dot egt-launch__dot--y" }, { n: "span", c: "egt-launch__dot egt-launch__dot--g" } ] },
        { n: "span", c: "egt-launch__barside", i: { n: "span", c: "egt-launch__title", i: "Start here" } }
      ] },
      { n: "nav", c: "egt-launch__body", "aria-label": "Start here", i: picks.map(function (c, i) {
        var sub = joinMeta([ fmtDay(c.date), c.runtime_display ]);
        return linkAttrs({ n: "a", c: "egt-launch__row egt-reveal egt-reveal--up", style: "--egt-i:" + i, "aria-label": (c.title || "") + ", " + sub, i: [
          { n: "span", c: "egt-launch__icon egt-pod-launch-art", "aria-hidden": "true",
            i: { n: "img", c: "egt-launch__img", src: img(c.card_image), alt: "", loading: "lazy", decoding: "async" } },
          { n: "span", c: "egt-launch__id", i: [ { n: "span", c: "egt-launch__name egt-underline", i: c.title || "" }, { n: "span", c: "egt-launch__sub", i: sub } ] },
          { n: "span", c: "egt-launch__go", "aria-hidden": "true", i: "›" }
        ] }, cardHref(c));
      }) }
    ] } }
  ] };
}

function seriesSection(show, bySlug) {
  var blocks = (show.series || []).map(function (s) {
    var eps = (s.slugs || []).map(function (sl) { return bySlug[sl]; }).filter(Boolean);
    if (!eps.length) { return null; }
    var total = eps.reduce(function (a, c) { return a + (c.runtime_seconds || 0); }, 0);
    return { n: "article", c: "egt-feat egt-pod-series", i: [
      { n: "p", c: "egt-card__k", i: joinMeta([ "Series", eps.length + " parts", total ? hoursMinutes(total) : "" ]) },
      { n: "h3", c: "egt-feat__title", i: s.title },
      { n: "ol", c: "egt-pod-series__list", i: eps.map(function (c) {
        var href = cardHref(c);
        var inner = [ { n: "span", c: "egt-pod-series__no", i: String(c.number || "") }, { n: "span", c: "egt-pod-series__t", i: c.title || "" },
          { n: "span", c: "egt-pod-series__m", i: c.runtime_display || "" } ];
        return { n: "li", i: href ? linkAttrs({ n: "a", i: inner }, href) : { n: "span", c: "egt-pod-series__row", i: inner } };
      }) }
    ] };
  }).filter(Boolean);
  if (!blocks.length) { return null; }
  return { n: "section", c: "egt-section", i: { n: "div", c: "egt__shell", i: [
    sectionHead("Series", "Listen in order", "Some ideas take more than one episode. These run as a set."),
    { n: "div", c: "egt-grid egt-reveal", i: blocks }
  ] } };
}

function allEpisodes(show, cards) {
  var seasonLabel = (cards[0] && cards[0].season_label) || "";
  return { n: "section", c: "egt-section egt-album-list", id: "episodes", i: { n: "div", c: "egt__shell", i: [
    sectionHead(seasonLabel || "Episodes", "All " + cards.length + " episodes", "Newest first. Numbers follow release order."),
    { n: "ol", c: "egt-album-trks egt-pod-trks", role: "list", "aria-label": show.name + " episodes", i: cards.map(episodeRow) },
    { n: "div", c: "egt-album-list__foot egt-reveal", i: { n: "div", c: "egt-album-list__stream", role: "group", "aria-label": "Listen to " + show.name, i: streamPills(show) } }
  ] } };
}

function hostsCards(show, ex) {
  if (!show.hosts || !show.hosts.length || !show.hosts.some(function (h) { return h.bio; })) { return null; }
  return { n: "section", c: "egt-section", i: { n: "div", c: "egt__shell", i: [
    sectionHead(show.hosts.length > 1 ? "Your hosts" : "Your host", ex.hostsTitle || (show.hosts.length > 1 ? "The hosts" : "The host"), ex.hostsLead || ""),
    { n: "div", c: "egt-grid egt-reveal", i: show.hosts.map(function (h) {
      return { n: "article", c: "egt-feat egt-pod-host", i: [
        { n: "img", c: "egt-pod-host__img", src: img(h.portrait), alt: h.alt || h.name, loading: "lazy", decoding: "async",
          style: "object-position:" + safePos(h.portrait_position) },
        { n: "p", c: "egt-card__k", i: joinMeta([ "Host", h.handle || "" ]) },
        { n: "h3", c: "egt-feat__title", i: h.name },
        h.bio ? { n: "p", c: "egt-feat__copy", i: h.bio } : ""
      ] };
    }) }
  ] } };
}

function safePos(p) { return /^\d{1,3}% \d{1,3}%$/.test(String(p || "")) ? p : "50% 30%"; }

function followPanel(show) {
  return { n: "section", c: "egt-section egt-album-signup", id: "follow", i: { n: "div", c: "egt__shell egt-reveal",
    i: { n: "div", c: "egt-glass egt-pod-follow", i: [
      { n: "p", c: "egt__eyebrow", i: "Follow the podcast" },
      { n: "h2", c: "egt-cap__title", i: [ "Watch or listen ", { n: "span", c: "egt__gold", i: "where you already do." } ] },
      { n: "p", c: "egt-cap__lead", i: "New episodes land on " + platformNames(show) + "." },
      { n: "div", c: "egt-album-hero__stream egt-pod-follow__stream", role: "group", "aria-label": "Follow " + show.name, i: streamPills(show) },
      show.presented_by ? { n: "p", c: "egt-cap__fine", i: show.presented_by } : ""
    ] } } };
}

function platformNames(show) {
  var pf = show.platforms || {};
  var names = [];
  (pf.order || []).forEach(function (k) { if (pf[k]) { names.push(k === "youtube" ? "YouTube" : k === "apple" ? "Apple Podcasts" : "Spotify"); } });
  if (names.length < 2) { return names[0] || "every major platform"; }
  return names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
}

function captureSection(ex) {
  if (!ex.capture) { return null; }
  var c = ex.capture;
  return { n: "section", c: "egt-section egt-album-signup", id: "join", i: { n: "div", c: "egt__shell egt-reveal",
    i: S.capture({ source: c.source, id: c.id, variant: "panel", consent: true, eyebrow: c.eyebrow, title: c.title,
      lead: c.lead, label: c.label, cta: c.cta, consentLabel: c.consentLabel }) } };
}

function classicmanExtras() {
  var CM = require("../elements/egt.classicman.js");
  return [ CM.connect(), CM.support() ];
}

function seriesSchema(show, O) {
  var raw = show.platforms || {};
  var pf = { apple: safeUrl(raw.apple), spotify: safeUrl(raw.spotify), youtube: safeUrl(raw.youtube), rss: safeUrl(raw.rss) };
  return { "@type": "PodcastSeries", "@id": O + show.url_path + "#podcast", name: show.feed_title || show.name,
    alternateName: show.feed_title && show.feed_title !== show.name ? show.name : undefined,
    url: O + show.url_path, description: show.description || show.show_line || "", image: O + img(show.cover),
    webFeed: pf.rss || undefined, publisher: { "@id": O + "/#organization" },
    author: (show.hosts || []).map(function (h) { return h.link === "/founder" ? { "@id": O + "/founder#person" } : { "@type": "Person", name: h.name }; }),
    sameAs: [ pf.apple, pf.spotify, pf.youtube ].filter(Boolean) };
}

function showView(data, banner) {
  var x = data.extra || {};
  var show = x.show || {};
  var cards = x.cards || [];
  var bySlug = cardsBySlug(cards);
  var ex = SHOW_EXTRAS[show.key] || {};
  var sections = [ showHero(show, cards.length) ];
  if (ex.classicman && show.mission) {
    sections.push({ n: "section", c: "egt-section egt-section--center", i: { n: "div", c: "egt__shell",
      i: { n: "blockquote", c: "egt-quote egt-reveal", i: show.mission } } });
  }
  sections.push(seasonsSection(show), hostsCards(show, ex), startHere(show, bySlug, ((x.catalog || {}).index || {}).background), seriesSection(show, bySlug));
  if (cards.length) { sections.push(allEpisodes(show, cards)); }
  if (ex.capture) { sections.push(captureSection(ex)); }
  if (ex.classicman) { sections = sections.concat(classicmanExtras()); sections.push(followPanel(show)); }
  sections.push(backLink("/podcasts", "Back to Podcasts"));
  var O = S.siteOrigin();
  var schema = S.jsonLd([
    { "@type": "WebPage", "@id": O + show.url_path + "#webpage", url: O + show.url_path, name: show.name,
      description: show.description || "", isPartOf: { "@id": O + "/#website" }, about: { "@id": O + show.url_path + "#podcast" } },
    seriesSchema(show, O),
    breadcrumbs([ { name: "Podcasts", url: "/podcasts" }, { name: show.name, url: show.url_path } ])
  ]);
  return page(sections.filter(Boolean), banner, schema);
}

/* ── episode /podcasts/<show>/<slug> ──────────────────────────────────────── */

function platformButton(href, label, glyph, primary) {
  return { n: "a", href: href, target: "_blank", rel: "noopener", c: "egt-cta egt-pod-pf" + (primary ? " egt-pod-pf--play" : ""),
    i: [ glyph, { n: "span", i: label } ] };
}

function actions(p, url, show) {
  var ctaHref = p.cta && safeUrl(p.cta.href, true);
  var hasCta = !!(ctaHref && p.cta.label);
  var yt = safeUrl(p.youtube_url), ap = safeUrl(p.apple_url), sp = safeUrl(p.spotify_url);
  var out = [];
  if (hasCta) {
    out.push(linkAttrs({ n: "a", c: "egt-cta egt-cta--primary", i: [ p.cta.label, { n: "span", c: "egt-cta__tick", "aria-hidden": "true", i: " ›" } ] }, ctaHref));
    out.push({ n: "span", c: "egt-pod-break", "aria-hidden": "true" });
  }
  if (yt) { out.push(platformButton(yt, "YouTube", ytGlyph(), !hasCta)); }
  if (ap) { out.push(platformButton(ap, "Apple Podcasts", podcastGlyph(), !hasCta)); }
  if (sp) { out.push(platformButton(sp, "Spotify", S.glyphs.spotify(), !hasCta)); }
  if (p.transcript && p.transcript.segments && p.transcript.segments.length) {
    out.push({ n: "a", href: "#transcript", c: "egt-cta egt-cta--ghost", i: "Transcript" });
  }
  var menu = [];
  if (p.chapters && p.chapters.length) { menu.push({ n: "a", href: "#chapters", i: "Chapters" }); }
  if (p.show_notes_html) { menu.push({ n: "a", href: "#notes", i: "Show notes" }); }
  menu.push({ n: "button", type: "button", "data-copy": url, i: "Copy link" });
  var rss = show && show.platforms && safeUrl(show.platforms.rss);
  if (rss) { menu.push({ n: "a", href: rss, target: "_blank", rel: "noopener", i: "RSS feed" }); }
  menu.push({ n: "a", href: "https://www.linkedin.com/sharing/share-offsite/?url=" + encodeURIComponent(url), target: "_blank", rel: "noopener", i: "Share on LinkedIn" });
  menu.push({ n: "p", c: "egt-pod-more__note", role: "status", "aria-live": "polite", i: "" });
  out.push({ n: "details", c: "egt-pod-more", i: [
    { n: "summary", c: "egt-cta egt-cta--ghost", i: [ "More", { n: "span", c: "egt-cta__tick", "aria-hidden": "true", i: " ▾" } ] },
    { n: "div", c: "egt-pod-more__menu", i: menu }
  ] });
  return { n: "div", c: "egt-pod-actions", role: "group", "aria-label": "Watch, listen and more", i: out };
}

function episodeHero(p, show, url) {
  var showName = (show && show.name) || "Podcast";
  var eyebrow = [ { n: "a", href: (show && show.url_path) || ("/podcasts/" + p.show), c: "egt-pod-eyebrow-link", i: showName } ];
  var rest = joinMeta([ p.season_label || "", p.number ? "Episode " + p.number : "" ]);
  if (rest) { eyebrow.push(" · " + rest); }
  var meta = joinMeta([ fmtDay(p.date), p.runtime_display, p.series ? "Part " + p.series.part + " of " + p.series.of + ", " + p.series.title : "" ]);
  var still = img(p.hero_still || p.card_image);
  var watchLabel = joinMeta([ p.youtube_id ? "Watch" : "Listen", p.runtime_display ]);
  var media = p.youtube_id
    ? { n: "a", href: "#watch", c: "egt-pod-still", "data-play": "true", "data-yt": p.youtube_id, "data-title": p.title,
        "aria-label": "Play " + p.title, "aria-haspopup": "dialog", i: [
        { n: "img", src: still, alt: "A still from the episode", width: "1280", height: "720", decoding: "async", fetchpriority: "high" },
        { n: "span", c: "egt-pod-bigplay", "aria-hidden": "true", i: playGlyph() },
        { n: "span", c: "egt-pod-still__label", "aria-hidden": "true", i: watchLabel } ] }
    : linkAttrs({ n: "a", c: "egt-pod-still", "aria-label": "Listen to " + p.title + " on Apple Podcasts", i: [
        { n: "img", src: still, alt: "A still from the episode", width: "1280", height: "720", decoding: "async", fetchpriority: "high" },
        { n: "span", c: "egt-pod-bigplay", "aria-hidden": "true", i: playGlyph() },
        { n: "span", c: "egt-pod-still__label", "aria-hidden": "true", i: watchLabel } ] }, safeUrl(p.apple_url) || "#watch");
  return { n: "section", c: "egt-section egt-canvas egt-pod-ephero", i: [
    S.media({ img: img(show && show.background), alt: "", priority: true, parallax: 0.06 }),
    { n: "div", c: "egt__shell egt-canvas__inner egt-pod-split", i: [
      { n: "div", c: "egt-glass egt-pod-split__text egt-reveal", i: [
        { n: "p", c: "egt__eyebrow", i: eyebrow },
        { n: "h1", c: "egt-album-hero__title egt-pod-ep-title", i: p.title },
        meta ? { n: "p", c: "egt-album-hero__count", i: meta } : "",
        p.summary ? { n: "p", c: "egt-pod-ep-summary", i: p.summary } : "",
        p.content_note ? { n: "p", c: "egt-pod-contentnote", i: p.content_note } : "",
        actions(p, url, show)
      ] },
      { n: "div", c: "egt-pod-split__media egt-reveal", style: "--egt-i: 1", i: media }
    ] }
  ] };
}

function relatedRow(p, bySlug) {
  var seen = {};
  var list = (p.related || []).map(function (r) {
    var c = bySlug[r.slug];
    if (!c || !c.page || seen[r.slug] || r.slug === p.slug) { return null; }
    seen[r.slug] = true;
    return { c: c, why: r.why };
  }).filter(Boolean).slice(0, 4);
  if (!list.length) { return null; }
  return { n: "section", c: "egt-section egt-pod-related", "aria-label": "Keep watching", i: { n: "div", c: "egt__shell", i: [
    { n: "p", c: "egt__eyebrow egt-pod-related__k", i: "Keep watching" },
    { n: "div", c: "egt-pod-rel", role: "list", "data-scroll-hint": "true", i: list.map(function (r, i) {
      var c = r.c;
      return { n: "a", c: "egt-pod-relc egt-reveal", role: "listitem", style: "--egt-i: " + i, href: c.url_path, i: [
        { n: "span", c: "egt-pod-relc__img", i: [
          { n: "img", src: img(c.card_image || c.hero_still), alt: "", loading: "lazy", decoding: "async", width: "480", height: "270" },
          { n: "span", c: "egt-pod-relc__play", "aria-hidden": "true", i: playGlyph() },
          c.runtime_display ? { n: "span", c: "egt-pod-relc__dur", i: c.runtime_display } : ""
        ] },
        { n: "span", c: "egt-pod-relc__body", i: [
          { n: "span", c: "egt-card__k", i: r.why },
          { n: "span", c: "egt-pod-relc__t", i: c.title || "" },
          { n: "span", c: "egt-pod-pn__m", i: joinMeta([ c.number ? "Episode " + c.number : "", fmtDay(c.date) ]) }
        ] }
      ] };
    }) }
  ] } };
}

function watchSection(p) {
  var chapters = (p.youtube_id && p.chapters) || [];
  var player = p.youtube_id
    ? { n: "div", c: "egt-pod-player", i: { n: "iframe", id: "egt-pod-player", src: ytEmbed(p.youtube_id), title: p.title + " on YouTube",
        loading: "lazy", referrerpolicy: "strict-origin-when-cross-origin",
        allow: "accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen", allowfullscreen: "allowfullscreen" } }
    : { n: "div", c: "egt-pod-player egt-pod-player--none", i: [
        { n: "img", src: img(p.hero_still || p.card_image), alt: "", loading: "lazy", decoding: "async" },
        safeUrl(p.apple_url) ? { n: "a", c: "egt-cta egt-pod-pf egt-pod-pf--play", href: safeUrl(p.apple_url), target: "_blank", rel: "noopener", i: [ podcastGlyph(), { n: "span", i: "Listen on Apple Podcasts" } ] } : ""
      ] };
  var grid = [ { n: "div", c: "egt-glass egt-pod-watch__player", i: player } ];
  if (chapters.length) {
    grid.push({ n: "div", c: "egt-pod-chaps", id: "chapters", i: { n: "div", c: "egt-pod-chaps__in", i: [
      { n: "p", c: "egt-card__k", i: "Chapters · " + chapters.length },
      { n: "ol", c: "egt-pod-chaps__list", role: "list", "aria-label": "Chapters", i: chapters.map(function (ch) {
        return { n: "li", i: { n: "a", c: "egt-pod-chap", href: ytWatch(p.youtube_id, ch.t), "data-t": String(ch.t),
          "aria-label": "Play from " + fmtTs(ch.t) + ": " + ch.title, i: [
          { n: "img", src: img(ch.still, img(p.card_image)), alt: "", loading: "lazy", decoding: "async", width: "480", height: "270" },
          { n: "span", c: "egt-pod-chap__txt", i: [ { n: "span", c: "egt-pod-chap__ts", i: fmtTs(ch.t) }, { n: "span", c: "egt-pod-chap__t", i: ch.title } ] }
        ] } };
      }) }
    ] } });
  }
  return { n: "section", c: "egt-section egt-pod-watch", id: "watch", i: { n: "div", c: "egt__shell",
    i: { n: "div", c: "egt-pod-watch__grid egt-reveal" + (chapters.length ? "" : " egt-pod-watch__grid--solo"), i: grid } } };
}

function tsLink(p, t) {
  var label = fmtTs(t);
  if (!p.youtube_id) { return { n: "span", c: "egt-pod-ts-link", i: label }; }
  return { n: "a", c: "egt-pod-ts-link", href: ytWatch(p.youtube_id, t), "data-t": String(Math.floor(t)), "aria-label": "Play from " + label, i: label };
}

function quotesSection(p) {
  if (!p.quotes || !p.quotes.length) { return null; }
  return { n: "section", c: "egt-section egt-pod-quotes", id: "quotes", i: { n: "div", c: "egt__shell", i: [
    sectionHead("From the episode", "Worth repeating", p.youtube_id ? "Word for word from the episode. Tap a time to play from that point." : "Word for word from the episode."),
    { n: "div", c: "egt-pod-quotes__list", i: p.quotes.slice(0, 3).map(function (q) {
      return { n: "figure", c: "egt-pod-q egt-reveal", i: [
        { n: "blockquote", c: "egt-quote egt-pod-q__text", i: "“" + q.text + "”" },
        { n: "figcaption", c: "egt-pod-q__cap", i: [ tsLink(p, q.t), " In this episode" ] }
      ] };
    }) }
  ] } };
}

function notesSection(p) {
  if (!p.show_notes_html) { return null; }
  var body = Notes.htmlToUA(p.show_notes_html);
  if (!body.length) { return null; }
  return { n: "section", c: "egt-section egt-pod-notes", id: "notes", i: { n: "div", c: "egt__shell", i: [
    sectionHead("Show notes", "In this episode"),
    { n: "div", c: "egt-glass egt-pod-prose egt-reveal", i: body }
  ] } };
}

function hostStrip(show) {
  var hosts = (show && show.hosts) || [];
  if (!hosts.length) { return null; }
  return { n: "section", c: "egt-section egt-pod-hostsec", i: { n: "div", c: "egt__shell", i: { n: "div", c: "egt-glass egt-pod-hosts egt-reveal", i: [
    { n: "p", c: "egt__eyebrow", i: hosts.length > 1 ? "Your hosts" : "Your host" },
    { n: "div", c: "egt-pod-hosts__list", i: hosts.map(function (h) {
      // The founder's page has a real portrait; other hosts use theirs from the record.
      var founder = h.link === "/founder";
      var inner = [
        { n: "span", c: "egt-pod-hostp__img",
          i: { n: "img", src: founder ? FOUNDER_PORTRAIT : img(h.portrait), alt: h.alt || h.name, loading: "lazy", decoding: "async",
            style: "object-position:" + (founder ? "50% 30%" : safePos(h.portrait_position)) } },
        { n: "span", c: "egt-pod-hostp__txt", i: [
          { n: "span", c: "egt-pod-hostp__n", i: h.name },
          { n: "span", c: "egt-pod-hostp__r", i: h.role + (h.alias ? " Also known as " + h.alias + "." : "") },
          (safeUrl(h.link, true) && /^\//.test(h.link)) ? { n: "span", c: "egt-cta egt-cta--text egt-pod-hostp__go", i: [ h.link === "/founder" ? "About the founder" : "More", { n: "span", c: "egt-cta__tick", "aria-hidden": "true", i: " ›" } ] } : ""
        ] }
      ];
      return safeUrl(h.link, true) && /^\//.test(h.link) ? { n: "a", c: "egt-pod-hostp", href: h.link, i: inner } : { n: "div", c: "egt-pod-hostp", i: inner };
    }) }
  ] } } };
}

function prevNext(p, bySlug) {
  function card(slug, dir) {
    var c = slug && bySlug[slug];
    if (!c || !c.page) { return { n: "span", c: "egt-pod-pn egt-pod-pn--empty" }; }
    return { n: "a", c: "egt-pod-pn egt-pod-pn--" + dir, href: c.url_path, i: [
      { n: "img", src: img(c.card_image || c.hero_still), alt: "", loading: "lazy", decoding: "async" },
      { n: "span", c: "egt-pod-pn__txt", i: [
        { n: "span", c: "egt-card__k", i: dir === "prev" ? [ { n: "span", "aria-hidden": "true", i: "‹ " }, "Previous episode" ] : [ "Next episode", { n: "span", "aria-hidden": "true", i: " ›" } ] },
        { n: "span", c: "egt-pod-pn__t", i: c.title || "" },
        { n: "span", c: "egt-pod-pn__m", i: joinMeta([ fmtDay(c.date), c.runtime_display ]) }
      ] }
    ] };
  }
  var prev = card(p.prev_slug, "prev"), next = card(p.next_slug, "next");
  if (prev.c.indexOf("--empty") > -1 && next.c.indexOf("--empty") > -1) { return null; }
  return { n: "section", c: "egt-section egt-pod-prevnext-sec", i: { n: "div", c: "egt__shell",
    i: { n: "nav", c: "egt-pod-prevnext egt-reveal", "aria-label": "Previous and next episode", i: [ prev, next ] } } };
}

function transcriptSection(p) {
  var tr = p.transcript;
  if (!tr || !tr.segments || !tr.segments.length) { return null; }
  var words = Number(tr.words) || 0;
  var lead = (tr.label || "This transcript may contain errors.") + (words ? " About " + words.toLocaleString("en-US") + " words." : "");
  return { n: "section", c: "egt-section egt-pod-tr", id: "transcript", i: { n: "div", c: "egt__shell", i: [
    sectionHead("Transcript", "Full transcript", lead),
    { n: "div", c: "egt-glass egt-pod-transcript egt-reveal", i: { n: "div", c: "egt-pod-prose egt-pod-transcript__body", role: "region", tabindex: "0",
      "aria-label": "Full transcript of " + p.title + ", scrollable",
      i: tr.segments.map(function (s) { return { n: "p", i: [ tsLink(p, s.t), " ", s.text ] }; }) } }
  ] } };
}

function episodeSchema(p, show, url, O) {
  var still = O + img(p.hero_still || p.card_image);
  var ep = { "@type": "PodcastEpisode", "@id": url + "#episode", url: url, name: p.title, description: p.summary || "",
    datePublished: p.date, timeRequired: isoDuration(p.runtime_seconds), image: still,
    partOfSeries: { "@id": O + ((show && show.url_path) || "/podcasts/" + p.show) + "#podcast" } };
  if (p.number) { ep.episodeNumber = String(p.number); }
  if (p.youtube_id) {
    ep.associatedMedia = { "@type": "VideoObject", name: p.title, description: p.summary || p.title, thumbnailUrl: still,
      uploadDate: p.published_at || p.date, duration: isoDuration(p.runtime_seconds),
      embedUrl: ytEmbed(p.youtube_id), contentUrl: safeUrl(p.youtube_url) || ytWatch(p.youtube_id) };
  }
  return ep;
}

function episodeView(data, banner) {
  var p = data.payload || {};
  var x = data.extra || {};
  var show = x.show || showOf(x, p.show) || { key: p.show, name: "Podcast", url_path: "/podcasts/" + p.show };
  var bySlug = cardsBySlug(x.cards);
  var O = S.siteOrigin();
  var url = O + (p.url_path || ("/podcasts/" + p.show + "/" + p.slug));
  var ex = SHOW_EXTRAS[show.key] || {};
  var sections = [
    episodeHero(p, show, url),
    relatedRow(p, bySlug),
    watchSection(p),
    quotesSection(p),
    notesSection(p),
    hostStrip(show),
    prevNext(p, bySlug),
    ex.capture ? captureSection(ex) : null,
    transcriptSection(p),
    backLink(show.url_path || "/podcasts", "Back to " + (show.name || "Podcasts"))
  ].filter(Boolean);
  var schema = S.jsonLd([
    { "@type": "WebPage", "@id": url + "#webpage", url: url, name: p.title, description: p.summary || "",
      isPartOf: { "@id": O + "/#website" }, about: { "@id": url + "#episode" }, datePublished: p.date },
    episodeSchema(p, show, url, O),
    breadcrumbs([ { name: "Podcasts", url: "/podcasts" }, { name: show.name || "Podcast", url: show.url_path || "/podcasts/" + p.show },
      { name: p.title, url: p.url_path || ("/podcasts/" + p.show + "/" + p.slug) } ])
  ]);
  return page(sections, banner, schema);
}

exports.index = indexView;
exports.show = showView;
exports.episode = episodeView;
